// apps/patient/src/components/locations/LocationForm.tsx

import { useState } from 'react';
import { X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { PatientLocation, PatientLocationType } from '@prism-ehr/shared';
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_PRESETS,
  LOCATION_TYPE_ICONS,
  LOCATION_TYPE_LABELS
} from '../../constants/equipment';

interface LocationFormProps {
  location?: PatientLocation | null;
  onSave: (data: Partial<PatientLocation>) => Promise<void>;
  onClose: () => void;
}

export function LocationForm({ location, onSave, onClose }: LocationFormProps) {
  const [name, setName] = useState(location?.name || '');
  const [locationType, setLocationType] = useState<PatientLocationType>(
    location?.location_type || 'home'
  );
  const [equipment, setEquipment] = useState<string[]>(location?.equipment || []);
  const [notes, setNotes] = useState(location?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const toggleEquipment = (item: string) => {
    setEquipment((prev) =>
      prev.includes(item)
        ? prev.filter((e) => e !== item)
        : [...prev, item]
    );
  };

  const toggleCategoryAll = (category: string) => {
    const categoryItems = EQUIPMENT_CATEGORIES[category] || [];
    const allSelected = categoryItems.every((item) => equipment.includes(item));

    if (allSelected) {
      setEquipment((prev) => prev.filter((e) => !categoryItems.includes(e)));
    } else {
      setEquipment((prev) => [...new Set([...prev, ...categoryItems])]);
    }
  };

  const applyPreset = (presetName: string) => {
    const presetEquipment = EQUIPMENT_PRESETS[presetName] || [];
    setEquipment(presetEquipment);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a location name');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await onSave({
        name: name.trim(),
        location_type: locationType,
        equipment,
        notes: notes.trim() || undefined,
        is_public: false,
        is_favorite: false,
        is_default: location?.is_default || false,
        status: 'active'
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save location');
      setSaving(false);
    }
  };

  const locationTypes: PatientLocationType[] = [
    'home',
    'commercial_gym',
    'workplace',
    'outdoor',
    'clinic',
    'other'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {location ? 'Edit Location' : 'Add New Location'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Home, Planet Fitness, Work Gym"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Location Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {locationTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setLocationType(type)}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    locationType === type
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-xl block mb-1">{LOCATION_TYPE_ICONS[type]}</span>
                  <span className="text-xs">{LOCATION_TYPE_LABELS[type]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Equipment Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Setup
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.keys(EQUIPMENT_PRESETS).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className="px-3 py-1.5 text-sm border rounded-full hover:bg-gray-50 transition-colors"
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          {/* Equipment Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Equipment ({equipment.length} selected)
            </label>
            <div className="border rounded-lg divide-y">
              {Object.entries(EQUIPMENT_CATEGORIES).map(([category, items]) => {
                const isExpanded = expandedCategories.includes(category);
                const selectedCount = items.filter((item) =>
                  equipment.includes(item)
                ).length;
                const allSelected = selectedCount === items.length;

                return (
                  <div key={category}>
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-sm">{category}</span>
                        {selectedCount > 0 && (
                          <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                            {selectedCount}/{items.length}
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-3 pt-1">
                        {/* Select All Toggle */}
                        <button
                          type="button"
                          onClick={() => toggleCategoryAll(category)}
                          className="text-xs text-primary hover:underline mb-2"
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </button>

                        <div className="flex flex-wrap gap-2">
                          {items.map((item) => {
                            const isSelected = equipment.includes(item);
                            return (
                              <button
                                key={item}
                                type="button"
                                onClick={() => toggleEquipment(item)}
                                className={`px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-1 ${
                                  isSelected
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                {isSelected && <Check className="h-3 w-3" />}
                                {item}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Open 24 hours, limited barbells on weekends"
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : location ? 'Update Location' : 'Add Location'}
          </button>
        </div>
      </div>
    </div>
  );
}
